// The app's SECURITY CONTEXT in the top bar — e.g. "Financial Entity: FE1 ▾".
//
// What a user may do in an application with context-based security depends on
// which entity they are signed into (see the engine's `security_context`), so
// that entity is always on screen, and one click away from switching. Shared
// by the in-platform Run view and the downloaded runtime client; the caller
// owns the data (the identity endpoint's `security_context` block + the option
// labels) and what "switch" opens (the Project settings dialog).
//
// Theme: CSS custom properties with literal fallbacks, like PromptModal.

import React from 'react'

export interface SecurityContextChipProps {
  /** The variable's prompt, e.g. "Financial Entity". */
  label: string
  /** The value signed into, or null when none is usable yet. */
  value: string | null
  /** The option label for `value` (e.g. "Entity One"), when known. */
  valueLabel?: string | null
  /** No usable value: the app is running on the user's global roles only. */
  required?: boolean
  onSwitch: () => void
}

export function SecurityContextChip({
  label, value, valueLabel, required, onSwitch,
}: SecurityContextChipProps) {
  const missing = required || !value
  const shown = missing ? `Choose ${label}` : (valueLabel || value)
  return (
    <button
      type="button"
      onClick={onSwitch}
      title={missing
        ? `Pick a ${label} — your rights depend on it`
        : `${label}: ${valueLabel || value}. Your rights here follow it — click to switch.`}
      data-tw-security-context={value || ''}
      style={{ ...chip, ...(missing ? chipWarn : null) }}
    >
      <span style={chipLabel}>{label}</span>
      <span style={chipValue}>{shown}</span>
      <span aria-hidden="true" style={{ opacity: 0.7 }}>▾</span>
    </button>
  )
}

export default SecurityContextChip

const chip: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: 260,
  padding: '4px 10px', borderRadius: 999, cursor: 'pointer',
  border: '1px solid var(--color-border, #d1d5db)',
  background: 'var(--color-bg-secondary, transparent)',
  color: 'var(--color-text, #111827)', fontSize: '0.8425rem', lineHeight: 1.2,
  whiteSpace: 'nowrap',
}
const chipWarn: React.CSSProperties = {
  borderColor: 'rgba(245, 158, 11, 0.7)', background: 'rgba(245, 158, 11, 0.12)',
}
const chipLabel: React.CSSProperties = {
  color: 'var(--color-text-secondary, #6b7280)',
}
const chipValue: React.CSSProperties = {
  fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis',
}
